import { useEffect, useRef, useState } from 'preact/hooks';
import * as echarts from 'echarts';
import type { ECharts, EChartsOption } from 'echarts';
import { themeSignal } from '../theme/theme';
import type { LeafDto } from '../api/types';
import { api, ApiError } from '../api/client';
import { SHAPE_LABEL, historyPath, toSeries, type LeafHistoryResponse, type SeriesPoint } from '../api/history';

/**
 * ECharts-backed history chart for one leaf (issue #13). Drop-in replacement
 * for the hand-rolled SVG {@link LeafHistoryChart}: same props, same
 * <c>GET /api/leaves/{id}/history</c> data source, same window buttons.
 *
 * Design notes:
 * - The chart instance is created once per mount and disposed on unmount;
 *   data + option changes flow through <c>setOption(..., { notMerge: false })</c>.
 * - Theme is read from CSS variables via <c>getComputedStyle</c> and
 *   re-applied whenever {@link themeSignal} flips — a subscribe on the signal
 *   keeps that reactive without forcing a Preact re-render.
 * - Width follows the container via <c>ResizeObserver</c>; ECharts owns the
 *   internal DPR / retina handling.
 * - Data is the shape (M4) at the container width, drawn as a step line with
 *   gaps: pixel-identical to the raw data, for display only.
 */

interface Props {
  leafId: string;
  leaf: LeafDto;
  onClose(): void;
}

interface ChartData {
  series: SeriesPoint[];
  holdAssumed: boolean;
}

type WindowKey = '15m' | '1h' | '6h' | '24h';
const WINDOWS: { key: WindowKey; label: string; ms: number }[] = [
  { key: '15m', label: '15 Min', ms: 15 * 60_000 },
  { key: '1h', label: '1 h', ms: 60 * 60_000 },
  { key: '6h', label: '6 h', ms: 6 * 60 * 60_000 },
  { key: '24h', label: '24 h', ms: 24 * 60 * 60_000 },
];

function readTokens() {
  // Read CSS custom properties from :root so ECharts can style itself with
  // the same colours as the rest of the shell. Falls back to sensible dark-
  // palette defaults if a token is missing (never observed in practice).
  const s = typeof window === 'undefined'
    ? null
    : getComputedStyle(document.documentElement);
  const get = (name: string, fallback: string) => (s?.getPropertyValue(name).trim() || fallback);
  return {
    surface: get('--surface-base', '#16191D'),
    text: get('--text-primary', '#E2E5EA'),
    border: get('--border-subtle', '#2E343B'),
    accent: get('--accent', '#77A22B'),
  };
}

export function EChartsHistoryChart({ leafId, leaf, onClose }: Props) {
  const [windowKey, setWindowKey] = useState<WindowKey>('1h');
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'ok'; data: ChartData }
    | { status: 'empty' }
    | { status: 'error'; message: string }
  >({ status: 'loading' });
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<ECharts | null>(null);

  // Fetch history whenever the window changes.
  useEffect(() => {
    const win = WINDOWS.find((w) => w.key === windowKey)!;
    const to = new Date();
    const from = new Date(to.getTime() - win.ms);
    const controller = new AbortController();
    setState({ status: 'loading' });
    (async () => {
      try {
        // The canvas is hidden while loading; its parent still has the width.
        const widthPx = containerRef.current?.parentElement?.clientWidth || 1000;
        const res = await api.get<LeafHistoryResponse>(
          historyPath(leafId, from, to, { mode: 'shape', widthPx }),
          controller.signal,
        );
        if (controller.signal.aborted) return;
        const series = toSeries(res);
        setState(series.every(([, v]) => v === null)
          ? { status: 'empty' }
          : { status: 'ok', data: { series, holdAssumed: res.holdSource === 'assumed' } });
      } catch (err) {
        if (controller.signal.aborted) return;
        if (err instanceof ApiError && err.status === 404) {
          setState({ status: 'empty' });
        } else {
          setState({ status: 'error', message: 'Verlauf konnte nicht geladen werden.' });
        }
      }
    })();
    return () => controller.abort();
  }, [leafId, windowKey]);

  // Chart lifecycle: init on mount, dispose on unmount. Re-apply option on
  // data or theme change without re-creating the instance.

  useEffect(() => {
    if (!containerRef.current) return;
    const chart = echarts.init(containerRef.current);
    chartRef.current = chart;

    const observer = new ResizeObserver(() => chart.resize());
    observer.observe(containerRef.current);

    const unsubscribeTheme = themeSignal.subscribe(() => {
      // Force a fresh setOption so token-driven colours are picked up.
      applyOption(chart, state, leaf);
    });

    return () => {
      unsubscribeTheme();
      observer.disconnect();
      chart.dispose();
      chartRef.current = null;
    };
    // Intentionally not listing `state` here: this effect handles lifetime
    // only. The follow-up effect applies option on state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!chartRef.current) return;
    applyOption(chartRef.current, state, leaf);
  }, [state, leaf]);

  return (
    <div class="hv-echart-history">
      <div class="hv-echart-history__toolbar">
        {WINDOWS.map((w) => (
          <button
            key={w.key}
            type="button"
            class={windowKey === w.key ? 'hv-btn hv-btn--sm is-active' : 'hv-btn hv-btn--sm'}
            onClick={() => setWindowKey(w.key)}
          >
            {w.label}
          </button>
        ))}
        <span class="hv-echart-history__legend muted">
          {state.status === 'ok'
            ? `${SHAPE_LABEL}${state.data.holdAssumed ? ' · Haltedauer angenommen' : ''}`
            : ''}
        </span>
        <button
          type="button"
          class="hv-btn hv-btn--sm hv-echart-history__close"
          onClick={onClose}
          aria-label="Schließen"
        >
          ×
        </button>
      </div>
      {state.status === 'loading' && (
        <div class="hv-echart-history__msg muted">Lade Verlauf…</div>
      )}
      {state.status === 'empty' && (
        <div class="hv-echart-history__msg muted">Keine Datenpunkte im gewählten Fenster.</div>
      )}
      {state.status === 'error' && (
        <div class="hv-echart-history__msg hv-echart-history__msg--error">{state.message}</div>
      )}
      <div
        ref={containerRef}
        class="hv-echart-history__canvas"
        style={{ display: state.status === 'ok' ? 'block' : 'none' }}
      />
    </div>
  );
}

function applyOption(
  chart: ECharts,
  state:
    | { status: 'loading' }
    | { status: 'ok'; data: ChartData }
    | { status: 'empty' }
    | { status: 'error'; message: string },
  leaf: LeafDto,
): void {
  if (state.status !== 'ok') {
    chart.clear();
    return;
  }
  const tokens = readTokens();
  const points = state.data.series;
  const unit = leaf.unit ?? '';
  const option: EChartsOption = {
    animation: false,
    backgroundColor: 'transparent',
    grid: { left: 48, right: 16, top: 12, bottom: 32, containLabel: true },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross' },
      backgroundColor: tokens.surface,
      borderColor: tokens.border,
      textStyle: { color: tokens.text },
      valueFormatter: (v) => (typeof v === 'number' ? `${v.toFixed(2)}${unit ? ' ' + unit : ''}` : '—'),
    },
    xAxis: {
      type: 'time',
      axisLine: { lineStyle: { color: tokens.border } },
      axisLabel: { color: tokens.text, hideOverlap: true },
      splitLine: { show: false },
    },
    yAxis: {
      type: 'value',
      scale: true,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        color: tokens.text,
        formatter: (v) => (unit ? `${v} ${unit}` : String(v)),
      },
      splitLine: { lineStyle: { color: tokens.border, opacity: 0.4 } },
    },
    series: [
      {
        type: 'line',
        name: leaf.displayName ?? leaf.symbol,
        showSymbol: false,
        step: 'end',
        lineStyle: { color: tokens.accent, width: 2 },
        areaStyle: { color: tokens.accent, opacity: 0.08 },
        data: points,
      },
    ],
  };
  chart.setOption(option, { notMerge: true });
}
